/**
 * NetDocuments Auth Store (Zustand)
 *
 * Centralised, singleton auth state for NetDocuments.
 * Validates the stored token with the server exactly ONCE (after app auth is
 * ready), then all consumers read the shared state.
 *
 * Mirrors the pattern used by useDocuSignStore.
 */

import { create } from 'zustand';
import { apiClient } from '@/api/client';
import {
  getStoredNdToken,
  storeNdToken,
  clearNdToken,
  isNdTokenExpired,
  refreshNdToken,
  ND_TOKEN_KEY,
  ND_AUTHENTICATED_KEY,
  ND_AUTH_CHANGED_EVENT,
} from '@/utils/ndTokenManager';

interface NetDocumentsState {
  isAuthenticated: boolean;
  isValidating: boolean;

  /** Validate the stored ND token with the server. Safe to call multiple times –
   *  only the first successful/failed validation actually hits the server.
   *  Subsequent calls return the cached result. Use `forceRevalidate` to bypass. */
  validateToken: () => Promise<boolean>;

  /** Force a fresh server validation (e.g. after OAuth popup stores a new token). */
  forceRevalidate: () => Promise<boolean>;

  /** Mark authenticated (e.g. after OAuth popup stores a fresh token). */
  setAuthenticated: (value: boolean) => void;
}

// Guard against concurrent validateToken calls (module-level, not per-instance)
let validationPromise: Promise<boolean> | null = null;

// Once validated successfully, don't re-validate on every call/event
let hasValidated = false;

/** Core validation logic – makes the actual server call. */
const doValidate = async (
  set: (state: Partial<NetDocumentsState>) => void,
): Promise<boolean> => {
  const token = getStoredNdToken();
  if (!token?.access_token) {
    // Service-account override – no user token needed
    if (localStorage.getItem('override-service-account') === 'true') {
      set({ isAuthenticated: true, isValidating: false });
      hasValidated = true;
      return true;
    }
    set({ isAuthenticated: false, isValidating: false });
    return false;
  }

  // If expired locally, try a silent refresh first
  if (isNdTokenExpired(token) && token.refresh_token) {
    const refreshed = await refreshNdToken(token.refresh_token);
    if (!refreshed) {
      set({ isAuthenticated: false, isValidating: false });
      return false;
    }
  }

  // Validate with server (single call)
  set({ isValidating: true });
  const current = getStoredNdToken();
  const accessToken = current?.access_token || token.access_token;

  try {
    const { data: result } = await apiClient.get(
      '/api/app/documents-provider/get-user-details',
      { params: { token: accessToken, provider: 'NetDocuments' } },
    );

    if (result?.isSuccess) {
      // Re-store with a fresh expires_on so future local checks pass.
      // NOTE: storeNdToken dispatches events — but hasValidated prevents re-entry.
      if (current) storeNdToken(current);
      set({ isAuthenticated: true, isValidating: false });
      hasValidated = true;
      return true;
    }

    // Server explicitly said the token is invalid (isSuccess: false)
    clearNdToken();
    set({ isAuthenticated: false, isValidating: false });
    hasValidated = true;
    return false;
  } catch {
    // Network error / Bearer token not ready yet / server unreachable.
    // Do NOT clear the ND token – it may still be valid.
    // Fall back to a local-only check: trust the token if it's not expired.
    if (!isNdTokenExpired(current || token)) {
      set({ isAuthenticated: true, isValidating: false });
      hasValidated = true;
      return true;
    }
    set({ isAuthenticated: false, isValidating: false });
    return false;
  }
};

export const useNetDocumentsStore = create<NetDocumentsState>()((set) => ({
  isAuthenticated: false,
  isValidating: false,

  setAuthenticated: (value) => set({ isAuthenticated: value }),

  validateToken: async (): Promise<boolean> => {
    // Already validated once this session — return cached state
    if (hasValidated) {
      return useNetDocumentsStore.getState().isAuthenticated;
    }

    // Deduplicate concurrent in-flight calls
    if (validationPromise) return validationPromise;

    validationPromise = doValidate(set);

    try {
      return await validationPromise;
    } finally {
      validationPromise = null;
    }
  },

  forceRevalidate: async (): Promise<boolean> => {
    // Reset so doValidate actually fires
    hasValidated = false;
    validationPromise = null;

    validationPromise = doValidate(set);
    try {
      return await validationPromise;
    } finally {
      validationPromise = null;
    }
  },
}));

// ── Bootstrap ────────────────────────────────────────────────────────────────
if (typeof window !== 'undefined') {
  // Quick local-only check so hooks have a value before server validation.
  // Server-side validation is deferred until AppShell sets up the Bearer token
  // and explicitly calls validateToken() — otherwise the request goes out
  // without auth and gets a 302 redirect to login.
  const initial = getStoredNdToken();
  if (initial?.access_token && !isNdTokenExpired(initial)) {
    useNetDocumentsStore.setState({ isAuthenticated: true });
  } else if (localStorage.getItem('override-service-account') === 'true') {
    useNetDocumentsStore.setState({ isAuthenticated: true });
  }

  // Listen for token changes (cross-tab via storage, same-tab via custom event)
  window.addEventListener('storage', (e) => {
    if (e.key === ND_AUTHENTICATED_KEY || e.key === ND_TOKEN_KEY) {
      // Only re-validate if we haven't already — prevents cross-tab loops
      if (!hasValidated) {
        useNetDocumentsStore.getState().validateToken();
      }
    }
  });

  window.addEventListener(ND_AUTH_CHANGED_EVENT, (e) => {
    const detail = (e as CustomEvent<{ authenticated: boolean }>).detail;
    if (detail?.authenticated) {
      // Token was just stored (e.g. after OAuth popup) – trust it, skip server call
      useNetDocumentsStore.setState({ isAuthenticated: true });
    } else {
      useNetDocumentsStore.setState({ isAuthenticated: false });
      // Reset so next validateToken call actually hits the server
      hasValidated = false;
    }
  });
}
